import { Directive, HostListener, input, computed } from "@angular/core";
import { StateService } from "../../services/state.service";
import { S25State, S25States } from "../../services/state/state.map";
import { S25Util } from "../../util/s25-util";
import { Timeout } from "../../decorators/timeout.decorator";
import { S25Const } from "../../util/s25-const";
import { NetworkHealthService, NetworkHealthStatus } from "../../services/network.health.service";

@Directive({
    selector: "a[s25-ng-router-link]",
    host: {
        "[attr.href]": "href()",
    },
    standalone: true,
})
// Use this to bridge the gap between Angular and AngJs state management
export class S25RouterLinkDirective<T extends S25States.Name = S25States.Name> {
    public readonly state = input.required({
        alias: "stateName",
        transform: (value: T): S25State => S25States.typed[value],
    });
    public readonly stateParams = input<S25States.ParamsForName<T>>();
    public readonly options = input<{
        // autocomplete these common params
        source?: string;
        search?: string;
        forceParamChange?: boolean;
        /** ui-router: re-resolve and re-enter the target (needed for some sticky transitions, e.g. Connect25 → dash). */
        reload?: boolean;
        [key: string]: string | boolean | undefined;
    }>();
    public readonly inheritParams = input<boolean>(false);

    public readonly href = computed(() => {
        let href = this.getLineage()
            .map((state) => state.url)
            .join("");
        let params = this.stateParams() ?? {};
        if (this.inheritParams()) params = { ...(StateService.getState().params ?? {}), ...params };
        Object.entries(params).forEach(([key, value]) => {
            if (S25Util.isDefined(value)) {
                // either `/url/:key/etc` or `/url/:key?/etc` or `/url/{key:...}/etc`
                const pattern = new RegExp(`(:${key}\\??|\{${key}:[^\}]+\})`, "g");
                href = href.replace(pattern, String(value));
            }
        });

        // Remove any remaining optional params that weren't provided (e.g., :mode?)
        href = href.replace(/\/:[^\/]+\?/g, "");

        return `${S25Const.baseUrl}#!${href}`;
    });

    // This link *should* bind the stateUrl to href attribute, or delegate to angular's router-link.
    // However, we want to intercept the behavior to call StateService.go so angularjs stays state aware on navigation
    @Timeout
    @HostListener("click", ["$event"])
    async onClick(event: MouseEvent) {
        if (event.ctrlKey || event.metaKey) return; // Allow ctrl/cmd+click to open in new tab
        event.preventDefault();

        if (S25Const.inApp && !NetworkHealthService.healthy()) NetworkHealthService.redisplayBanner();

        const [rslt, err] = await S25Util.Maybe(
            StateService.go(this.state().name, this.stateParams(), this.options(), false, this.inheritParams()),
        );
        if (err) {
            console.error("Error navigating to state:", err);
            return;
        }
        if (rslt) {
            // Prevent default action if navigation was successful
            event.stopPropagation();
        }
    }

    private getLineage(): Array<S25State> {
        let state: S25State = this.state();
        const lineage = [state];
        let key = state.parent;
        while (key !== "appRoot" && key !== "external") {
            state = S25States.typed[key];
            lineage.push(state);
            key = state.parent;
        }
        return lineage.reverse();
    }
}
